/**
 * WCAG 2.1 AA — SC 2.4.3 Focus Order (SPA route change).
 *
 * En una SPA el navegador no reinicia el foco al cambiar de ruta, así que un
 * usuario de lector de pantalla / teclado no percibe que la acción ha tenido
 * efecto. Este módulo mueve el foco, tras cada cambio de ruta, al primer
 * elemento *interactivo* visible dentro del componente montado por el nuevo
 * <router-view />.
 *
 * Nunca se mueve el foco a wrappers ni a elementos sin función (contenedores
 * con tabindex="-1", <main>, etc.) para no marear al usuario asistido.
 */

const FOCUSABLE_SELECTOR = [
  'a[href]',
  //   'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  //   'iframe',
  //   'audio[controls]',
  //   'video[controls]',
  //   '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])'
].join(',')

// Vistas cuyo contenido debe evitarse como destino de foco. Si el ancestro más
// próximo del elemento coincide con alguno de estos selectores, se descarta
// (evita capturar botones de la aside, header, chatbot, etc. en caso de que
// el $el consultado incluyese algo más que la propia vista).
const EXCLUDED_ANCESTOR_SELECTOR = [
  '[role="dialog"]',
  '[aria-hidden="true"]',
  '[inert]'
].join(',')

function isFocusableVisible (el) {
  if (!el || !el.getBoundingClientRect) return false
  if (el.hasAttribute('disabled')) return false
  if (el.getAttribute('aria-hidden') === 'true') return false
  if (el.closest && el.closest(EXCLUDED_ANCESTOR_SELECTOR)) return false
  const style = window.getComputedStyle(el)
  if (style.display === 'none' || style.visibility === 'hidden') return false
  const rect = el.getBoundingClientRect()
  if (rect.width === 0 && rect.height === 0) return false
  return true
}

function findFirstFocusable (root) {
  if (!root || typeof root.querySelectorAll !== 'function') return null
  const candidates = root.querySelectorAll(FOCUSABLE_SELECTOR)
  for (let i = 0; i < candidates.length; i++) {
    if (isFocusableVisible(candidates[i])) return candidates[i]
  }
  return null
}

function focusPreservingScroll (el) {
  if (!el) return false
  const x = window.scrollX
  const y = window.scrollY
  // `focusVisible: true` fuerza :focus-visible en navegadores que lo soportan
  // (Firefox 104+, Chrome 130+). Opción ignorada silenciosamente en el resto.
  try { el.focus({ preventScroll: true, focusVisible: true }) } catch (e) { el.focus() }
  window.scrollTo(x, y)
  const focused = document.activeElement === el
  if (focused) ensureFocusVisibleStyle(el)
  return focused
}

/**
 * Cuando .focus() se dispara por JS tras un clic de ratón, la mayoría de
 * navegadores mantienen la modalidad "mouse" y :focus-visible NO matchea
 * en anchors/botones, por lo que el contorno accesible no se pinta.
 * Detectamos ese caso y forzamos el estilo vía `data-a11y-forced-focus`
 * (la regla CSS vive en `src/assets/scss/overrides.scss`). Se limpia en
 * blur o al primer pointerdown para no ensuciar el DOM.
 */
function ensureFocusVisibleStyle (el) {
  let matches = false
  try { matches = el.matches(':focus-visible') } catch (e) { matches = false }
  if (matches) return
  el.setAttribute('data-a11y-forced-focus', '')
  const cleanup = () => {
    el.removeAttribute('data-a11y-forced-focus')
    el.removeEventListener('blur', cleanup, true)
    el.removeEventListener('pointerdown', cleanup, true)
    el.removeEventListener('mousedown', cleanup, true)
  }
  el.addEventListener('blur', cleanup, true)
  el.addEventListener('pointerdown', cleanup, true)
  el.addEventListener('mousedown', cleanup, true)
}

/**
 * Devuelve el $el del componente montado por el <router-view /> más profundo
 * de la ruta destino. Puede ser null hasta que el componente async se resuelve
 * y se monta.
 */
function getRouteViewEl (to) {
  const record = to.matched && to.matched[to.matched.length - 1]
  const instance = record && record.instances && record.instances.default
  const el = instance && instance.$el
  return (el && el.nodeType === 1) ? el : null
}

/**
 * Estrategia: 
 * 1) Espera al siguiente frame (rAF) para dejar terminar rerenders síncronos
 *    que suelen ocurrir tras el `mounted` (fetch inicial, autofocus, dialogos
 *    de mount, etc.). 
 * 2) Intenta enfocar el primer interactivo dentro del $el de la vista.
 * 3) Si el $el aún no existe (component async cargando) o no tiene ningún
 *    interactivo visible todavía, monta un MutationObserver sobre el body
 *    esperando cambios en subtree, con reintentos limitados por si el
 *    contenido llega en varias tandas (típico en detail views con múltiples
 *    fetchs). Se cancela tras un tope de tiempo para no dejar observers
 *    colgados.
 */
function moveFocusOnRouteChange (to, options = {}) {
  const {
    // Tope duro para dejar de intentar (async imports lentos, fetch, etc.)
    maxWaitMs = 2000,
    // Tras mover el foco con éxito, seguimos vigilando este tiempo para
    // "ganar" a un autofocus tardío que quisiera robar el foco.
    settleMs = 250
  } = options

  const started = Date.now()
  let focusedEl = null
  let observer = null
  let settleTimer = null
  let doneHardStop = null

  const cleanup = () => {
    if (observer) { observer.disconnect(); observer = null }
    if (settleTimer) { clearTimeout(settleTimer); settleTimer = null }
    if (doneHardStop) { clearTimeout(doneHardStop); doneHardStop = null }
  }

  const attempt = () => {
    if (Date.now() - started > maxWaitMs) { cleanup(); return }
    const rootEl = getRouteViewEl(to)
    if (!rootEl) return // esperamos a más mutaciones
    const target = findFirstFocusable(rootEl)
    if (!target) return
    // Si el foco actual ya está dentro del nuevo view sobre un elemento
    // interactivo válido, no hace falta moverlo (evita saltos innecesarios
    // cuando un mounted-focus del propio componente ya hizo el trabajo bien).
    const active = document.activeElement
    if (active && active !== document.body && rootEl.contains(active) &&
        active.matches && active.matches(FOCUSABLE_SELECTOR) && isFocusableVisible(active)) {
      focusedEl = active
    } else if (focusPreservingScroll(target)) {
      focusedEl = target
    }
    // Continuamos observando durante settleMs para reponer foco si alguien
    // lo roba después (setTimeout tardío en componentes hijos).
    if (focusedEl && !settleTimer) {
      settleTimer = setTimeout(() => { cleanup() }, settleMs)
    }
  }

  // Reintento tras cada mutación relevante del DOM del app.
  const scheduleObserver = () => {
    const appRoot = document.getElementById('app') || document.body
    observer = new MutationObserver(() => { attempt() })
    observer.observe(appRoot, { childList: true, subtree: true })
  }

  // Hard stop absoluto por si el observer sigue disparando cambios pequeños.
  doneHardStop = setTimeout(cleanup, maxWaitMs + settleMs + 100)

  // Espera un frame para dejar pasar rerenders síncronos del mounted.
  requestAnimationFrame(() => {
    attempt()
    if (!focusedEl) scheduleObserver()
  })
}

/**
 * Registra el hook global en el router. Llamar UNA sola vez tras crear el
 * router.
 */
export function installRouteFocus (router) {
  router.afterEach((to) => {
    moveFocusOnRouteChange(to)
  })
}

// Exports auxiliares por si en el futuro se quisiera invocar manualmente
// (por ejemplo tras cerrar un modal a pantalla completa).
export { moveFocusOnRouteChange, findFirstFocusable, focusPreservingScroll, FOCUSABLE_SELECTOR }
